import { axiosApiInstance } from "../axios";

export class PdfDownloadError extends Error {
  constructor(
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "PdfDownloadError";
  }
}

function filenameFromDisposition(disposition: unknown, fallbackFilename: string): string {
  if (typeof disposition !== "string") return fallbackFilename;

  const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encodedFilename) {
    try {
      return decodeURIComponent(encodedFilename.trim());
    } catch {
      // Fall through to the regular filename or safe default.
    }
  }

  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]?.trim();
  return filename || fallbackFilename;
}

async function errorMessageFromResponse(data: unknown, fallbackMessage: string): Promise<string> {
  let payload = data;

  if (payload instanceof Blob) {
    try {
      payload = JSON.parse(await payload.text());
    } catch {
      return fallbackMessage;
    }
  } else if (typeof payload === "string") {
    const stringPayload = payload;
    try {
      payload = JSON.parse(stringPayload);
    } catch {
      return stringPayload || fallbackMessage;
    }
  }

  if (payload && typeof payload === "object") {
    const candidate = payload as { error?: unknown; message?: unknown };
    const message = candidate.message ?? candidate.error;
    if (typeof message === "string" && message.trim()) return message;
  }

  return fallbackMessage;
}

function isApiError(error: unknown): error is {
  isAxiosError: true;
  response?: { data?: unknown; status?: number };
} {
  return Boolean(
    error &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
}

export async function requestPdfDownload(
  endpoint: string,
  fallbackFilename: string,
  fallbackErrorMessage: string
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await axiosApiInstance.post<Blob>(
      endpoint,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}`,
        },
        responseType: "blob",
      }
    );

    return {
      blob: response.data,
      filename: filenameFromDisposition(response.headers["content-disposition"], fallbackFilename),
    };
  } catch (error: unknown) {
    if (isApiError(error)) {
      throw new PdfDownloadError(
        await errorMessageFromResponse(error.response?.data, fallbackErrorMessage),
        error.response?.status
      );
    }

    throw new PdfDownloadError(fallbackErrorMessage);
  }
}

export function downloadPdf(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.href = objectUrl;
  downloadLink.download = filename;
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
